import React, { useCallback, useReducer, useEffect } from 'react'
import { Box, Divider, Stack, Link } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { uniqWith } from 'lodash'
import { isWithinInterval, startOfDay, endOfDay, min as getMinDate } from '@e3dc-react/shell/Libs/date'
import { Filter } from '@e3dc-react/shell/Elements/Filter'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AuthType, useChargingSessionsV1 } from 'src/Libs/e-mobility/charging-sessions'
import { ChargeDurationCard } from './stat-cards/ChargeDurationCard'
import { TotalEnergyCard } from './stat-cards/TotalEnergyCard'
import { SolarEnergyCard } from './stat-cards/SolarEnergyCard'
import ChargingSessionChartContainer from './ChargingSessionChartContainer'
import EMobilityChargingSessionsSkeletonV1 from './EMobilityChargingSessionsSkeletonV1'
import ChargingSessionTable from './ChargingSessionTable'
import { DateRangeFilterButton, DateRangeValue } from '@e3dc-react/shell/Elements/DatePicker'
import ExportChargingSessionsButton from './export-charging-sessions/ExportChargingSessionsButton'
import DischargedEnergyCard from './stat-cards/DischargedEnergyCard'

const useStyles = makeStyles()(theme => ({
  divider: {
    borderColor: theme.palette.divider,
  },
  cardStats: {
    marginTop: theme.spacing(2),
  },
  clearFiltersButton: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    alignSelf: 'center',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
}))

type TableState = {
  filters: {
    status: string[]
    authType: AuthType[]
    wallboxID: string[]
    dateRange: DateRangeValue | undefined
  }
  currPage: number
}

type ColumnFilterAction =
  | { type: 'SET_STATUS_FILTER'; value: string[] }
  | { type: 'SET_AUTH_TYPE_FILTER'; value: AuthType[] }
  | { type: 'SET_WALLBOX_IDS'; value: string[] }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_CURRENT_PAGE'; page: number }
  | { type: 'SET_DATE_RANGE'; dateRange: DateRangeValue | undefined }

const initialTableState: TableState = {
  filters: {
    status: [],
    authType: [],
    wallboxID: [],
    dateRange: undefined,
  },
  currPage: 0,
}

function tableStateReducer(state: TableState, action: ColumnFilterAction): TableState {
  switch (action.type) {
    case 'SET_STATUS_FILTER':
      return { ...state, filters: { ...state.filters, status: action.value }, currPage: 0 }
    case 'SET_AUTH_TYPE_FILTER':
      return { ...state, filters: { ...state.filters, authType: action.value }, currPage: 0 }
    case 'SET_WALLBOX_IDS':
      return { ...state, filters: { ...state.filters, wallboxID: action.value }, currPage: 0 }
    case 'CLEAR_ALL':
      return initialTableState
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currPage: action.page,
      }
    case 'SET_DATE_RANGE':
      return {
        ...state,
        filters: {
          ...state.filters,
          dateRange: action.dateRange,
        },
        currPage: 0,
      }
    default:
      return state
  }
}

interface EMobilityChargingSessionsProps {
  storageId: string
}

const EMobilityChargingSessions = ({ storageId }: EMobilityChargingSessionsProps): React.ReactElement => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { enqueueErrorStack } = useSnackbar()
  const [tableState, dispatch] = useReducer(tableStateReducer, initialTableState)
  const hasColumnFilters =
    tableState.filters.status.length > 0 ||
    tableState.filters.authType.length > 0 ||
    tableState.filters.wallboxID.length > 0 ||
    Boolean(tableState.filters.dateRange)
  const { chargingSessions, wallboxes, isLoading: chargingSessionsLoading, isError: chargingSessionsError } = useChargingSessionsV1(storageId)
  const totalChargeTime = chargingSessions ? chargingSessions.reduce((a, b) => a + b.chargeTimeActive, 0) : 0
  const totalEnergyKWh = chargingSessions ? chargingSessions.reduce((a, b) => a + b.energyAll, 0) / 1000 : 0
  const totalSolarEnergyKWh = chargingSessions ? chargingSessions.reduce((a, b) => a + b.energySolar, 0) / 1000 : 0
  const hasDischargeEnergy = chargingSessions && chargingSessions.some(item => Number((item.energyDischarged / 1000).toFixed(2)) > 0)
  const totalDischargedEnergy = chargingSessions ? chargingSessions.reduce((a, b) => a + b.energyDischarged, 0) / 1000 : 0

  const sessionsFilteredByStatus = (chargingSessions || []).filter(session => {
    if (tableState.filters.status.length === 0) {
      return true
    }

    return tableState.filters.status.includes(session.status)
  })

  const sessionsFilteredByAuthType = sessionsFilteredByStatus.filter(session => {
    if (tableState.filters.authType.length === 0) {
      return true
    }

    return tableState.filters.authType.includes(session.authentication.type)
  })

  const sessionsFilteredByWallbox = sessionsFilteredByAuthType.filter(session => {
    if (tableState.filters.wallboxID.length === 0) {
      return true
    }

    return tableState.filters.wallboxID.includes(session.wallbox.id)
  })

  const filteredChargingSessions =
    sessionsFilteredByWallbox.filter(session => {
      if (!tableState.filters.dateRange?.startDate || !tableState.filters.dateRange?.endDate) {
        return true
      }

      return (
        session.startAt &&
        isWithinInterval(new Date(session.startAt), {
          start: startOfDay(tableState.filters.dateRange.startDate),
          end: endOfDay(tableState.filters.dateRange.endDate),
        })
      )
    }) || []

  const minDate = getMinDate((chargingSessions || []).filter(s => s.startAt).map(session => new Date(session.startAt!)))
  const maxDate = endOfDay(new Date())

  const statusOptions = uniqWith(chargingSessions, (sessionA, sessionB) => sessionA.status === sessionB.status).map(session => ({
    label: t(`emobility.chargingSession.chargingSessionStatus.${session.status}`),
    value: session.status,
  }))
  const authenticationOptions = uniqWith(chargingSessions, (sessionA, sessionB) => sessionA.authentication.type === sessionB.authentication.type).map(
    session => ({
      label: t(`emobility.chargingSession.authType.${session.authentication.type}`),
      value: session.authentication.type,
    })
  )
  const wallboxOptions = uniqWith(chargingSessions, (sessionA, sessionB) => {
    return sessionA.wallbox.id === sessionB.wallbox.id
  }).map(session => ({
    value: session.wallbox.id,
    label: session.wallbox.name || t('emobility.chargingSession.wallboxId', { id: session.wallbox.id }) || '',
  }))

  const setResultPage = useCallback((page: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', page })
  }, [])

  useEffect(() => {
    if (!chargingSessionsLoading && chargingSessionsError) {
      enqueueErrorStack(t('emobility.chargingSession.chargingSessionFetchError'))
    }
  }, [chargingSessionsError, chargingSessionsLoading, enqueueErrorStack, t])

  if (chargingSessionsLoading || !chargingSessions || !wallboxes) {
    return <EMobilityChargingSessionsSkeletonV1 />
  }

  return (
    <Box>
      <Stack direction="row" justifyContent={'space-between'} alignItems={'center'} mt={2} mb={2}>
        <Stack direction="row" spacing={2}>
          <Filter
            label={t('emobility.chargingSession.status')}
            options={statusOptions}
            value={tableState.filters.status}
            onChange={value => {
              dispatch({ type: 'SET_STATUS_FILTER', value })
            }}
            disabled={chargingSessionsLoading}
          />
          <Filter
            label={t('emobility.chargingSession.authentication')}
            options={authenticationOptions}
            value={tableState.filters.authType}
            onChange={value => {
              dispatch({ type: 'SET_AUTH_TYPE_FILTER', value })
            }}
            disabled={chargingSessionsLoading}
          />
          <Filter
            label={t('emobility.chargingSession.wallbox')}
            options={wallboxOptions}
            value={tableState.filters.wallboxID}
            onChange={value => {
              dispatch({ type: 'SET_WALLBOX_IDS', value })
            }}
            disabled={chargingSessionsLoading}
          />
          <DateRangeFilterButton
            title={t('common.date')}
            value={tableState.filters.dateRange}
            minDate={minDate}
            maxDate={maxDate}
            onChange={dateRange => {
              dispatch({ type: 'SET_DATE_RANGE', dateRange })
            }}
          />

          {hasColumnFilters ? (
            <ClearFiltersButton
              onClick={() => {
                dispatch({ type: 'CLEAR_ALL' })
              }}
            />
          ) : null}
        </Stack>
        <ExportChargingSessionsButton sessions={filteredChargingSessions} />
      </Stack>

      <Divider className={classes.divider} />

      <Stack direction="row" rowGap={2} columnGap={2} className={classes.cardStats} flexWrap="wrap">
        <ChargeDurationCard chargeDurationMilliseconds={totalChargeTime} />
        <TotalEnergyCard energyKWh={totalEnergyKWh} />
        <SolarEnergyCard energyKWh={totalSolarEnergyKWh} />
        {hasDischargeEnergy && <DischargedEnergyCard dischargedEnergyKwh={totalDischargedEnergy} />}
      </Stack>

      <Box className={classes.tableContainer}>
        <ChargingSessionTable
          chargingSessions={filteredChargingSessions}
          wallboxes={wallboxes}
          resultPage={tableState.currPage}
          setResultPage={setResultPage}
        />
      </Box>

      <ChargingSessionChartContainer chargingSessions={chargingSessions} />
    </Box>
  )
}

const ClearFiltersButton: React.VFC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  return (
    <Link underline="none" variant="caption" className={classes.clearFiltersButton} onClick={onClick}>
      {t('filter.clearAllButtonText')}
    </Link>
  )
}

export default EMobilityChargingSessions
